// Wat accu + zon hebben OPGELEVERD - voor de klant hét getal achter de
// investering. De som is een eerlijk tegenfeit (dezelfde uren geprijsd
// zonder accu, en zonder accu én zon - zie backend queries.Savings);
// daarom durft de kaart 'm groot te tonen.
import { useEffect, useState } from "react";
import { fetchSavings, type Savings } from "../api";
import { Card, SectionTitle } from "./ui";

const RANGES = [
  { key: "today", label: "Vandaag" },
  { key: "month", label: "Deze maand" },
  { key: "year", label: "Dit jaar" },
  // "Alles" = sinds de vroegste data (echt of VRM-backfill) - de
  // backend bepaalt de startdatum, de ondertitel toont hem.
  { key: "all", label: "Alles" },
] as const;

function euro(v: number): string {
  return `€${v.toFixed(2).replace(".", ",")}`;
}

export function SavingsCard() {
  const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("month");
  const [data, setData] = useState<Savings | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    fetchSavings(range)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [range]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <SectionTitle>Jouw besparing</SectionTitle>
        <div className="flex rounded-lg bg-card-soft p-0.5 text-xs">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`px-2.5 py-1 rounded-md ${
                range === r.key ? "bg-card text-ink font-medium" : "text-ink-mute"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <Card className="p-4">
        {data == null ? (
          <div className="h-16 animate-pulse rounded-md bg-card-soft" />
        ) : data.contract == null ? (
          <p className="text-sm text-ink-mute">
            Geen energiecontract geconfigureerd - vraag je installateur de
            prijsinstellingen in te vullen.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div>
              <div className="text-3xl font-semibold tabular">
                {euro(data.total_eur)}
              </div>
              <div className="text-xs text-ink-mute mt-0.5">
                bespaard t.o.v. hetzelfde huis zonder accu en zonnepanelen
                {range === "all" && data.start_utc && (
                  <> · sinds{" "}
                    {new Date(data.start_utc).toLocaleDateString("nl-NL", {
                      day: "numeric", month: "short", year: "numeric",
                    })}
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-card-soft p-3">
                <div className="text-xs text-ink-mute">Accu</div>
                <div className="font-semibold tabular">{euro(data.battery_eur)}</div>
                <div className="text-[11px] text-ink-mute mt-0.5">
                  {data.battery_shifted_kwh.toFixed(0)} kWh verschoven naar
                  goedkope uren
                </div>
              </div>
              <div className="rounded-lg bg-card-soft p-3">
                <div className="text-xs text-ink-mute">Zon</div>
                <div className="font-semibold tabular">{euro(data.pv_eur)}</div>
                <div className="text-[11px] text-ink-mute mt-0.5">
                  {data.pv_generated_kwh.toFixed(0)} kWh zelf opgewekt
                </div>
              </div>
            </div>
            {data.battery_avoided_import_eur != null && (
              <BatteryBreakdown data={data} />
            )}
          </div>
        )}
      </Card>
    </section>
  );
}


// Wat de accu WERKELIJK veranderde, in drie regels die exact optellen
// tot het accu-bedrag (klantvraag 2026-10-26: "ik had toch alles 's
// avonds moeten inkopen?" - ja, maar zonder accu was de zon ook
// verkocht en de netlading niet gekocht).
function BatteryBreakdown({ data }: { data: Savings }) {
  const avoided = data.battery_avoided_import_eur ?? 0;
  const grid = data.battery_grid_charge_eur ?? 0;
  const pv = data.battery_unsold_pv_eur ?? 0;
  const row = (label: string, kwh: number | undefined, eur: number, sign: "+" | "-") => (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-ink-mute">
        {label}
        {kwh != null && kwh > 0 ? ` (${kwh.toFixed(0)} kWh)` : ""}
      </span>
      <span className="tabular whitespace-nowrap">{sign} {euro(Math.abs(eur))}</span>
    </div>
  );
  return (
    <div className="rounded-lg bg-card-soft p-3 text-xs flex flex-col gap-1">
      <div className="text-ink-mute font-medium mb-0.5">Wat de accu veranderde, vergeleken met hetzelfde huis zonder accu</div>
      {row("Inkoop vermeden: uit de accu in plaats van van het net", data.battery_discharged_kwh, avoided, "+")}
      {row("Min: stroom die de accu uit het net laadde", data.battery_grid_charged_kwh, grid, "-")}
      {row("Min: zon die anders meteen verkocht was", data.battery_pv_charged_kwh, pv, "-")}
      <div className="flex items-baseline justify-between gap-2 border-t border-line pt-1 mt-0.5 font-semibold text-ink">
        <span>Besparing door de accu</span>
        <span className="tabular">{euro(data.battery_eur)}</span>
      </div>
    </div>
  );
}
